import type { CelestialObject } from "../../components/types.js"
import { getLocalJSON, storeLocalJSON } from "../../storage/localStorage.js"
import type { LocationPick } from "./location.js"

export type QuickPick = {
  recordNumber: string
  name: string
  kind?: string
  designation?: string
  // For STAR/DSO quick picks (fixed targets), persist coordinates so they can be re-used.
  raDeg?: number
  decDeg?: number
  otype?: string
}

export interface TrackerSettings {
  selectedObject?: CelestialObject
  interval: string
  coordinateFormat: "hms" | "decimal"
  siteCode: string
  customLat: number | null
  customLon: number | null
  customElev: number
  useCustomCoords: boolean
  observatoryName: string | null
  timezoneMode: "utc" | "scope" | "local"
}

export const QUICK_PICKS_STORAGE_KEY = "celestial-quick-picks"
export const LOCATION_PICKS_STORAGE_KEY = "celestial-location-picks"
export const TRACKER_SETTINGS_KEY = "celestial-tracker-settings"

/**
 * Migration map for Horizons database IDs that have changed.
 * Maps old recordNumber -> new recordNumber.
 */
const QUICK_PICK_ID_MIGRATIONS: Record<string, string> = {
  "90004922": "90004923", // 3I/ATLAS (Jan 2026 - ID reassigned to PANSTARRS)
  // Add other known migrations here as needed
}

function normalizeLocationPick(raw: unknown): LocationPick | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>

  // Normalize type (older versions may have omitted it or used different casing)
  const typeRaw = typeof r.type === "string" ? r.type.toLowerCase() : null

  // Common legacy keys for MPC site code
  const legacySiteCode =
    (typeof r.siteCode === "string" && r.siteCode) ||
    (typeof r.mpcCode === "string" && r.mpcCode) ||
    (typeof r.code === "string" && r.code) ||
    (typeof r.site_code === "string" && r.site_code) ||
    ""

  const label =
    (typeof r.label === "string" && r.label) ||
    (typeof r.name === "string" && r.name) ||
    ""

  // If it looks like an MPC pick, prefer that
  if (typeRaw === "mpc" || (!!legacySiteCode && typeRaw !== "custom")) {
    const siteCode = legacySiteCode.trim().toUpperCase()
    if (!siteCode) return null
    return {
      type: "mpc",
      label: label || siteCode,
      siteCode,
      observatoryName:
        typeof r.observatoryName === "string" ? r.observatoryName : undefined,
    }
  }

  // Otherwise treat as custom pick if it has coordinates
  const lat = typeof r.lat === "number" ? r.lat : null
  const lon = typeof r.lon === "number" ? r.lon : null
  const elev =
    typeof r.elev === "number"
      ? r.elev
      : typeof r.elevation === "number"
        ? r.elevation
        : null

  if (typeRaw === "custom" || (lat !== null && lon !== null)) {
    if (lat === null || lon === null) return null
    return {
      type: "custom",
      label: label || "Custom Location",
      lat,
      lon,
      elev: elev ?? 0,
    }
  }

  return null
}

/**
 * Apply ID migrations to a single quick pick.
 */
function migrateQuickPick(pick: QuickPick): QuickPick {
  const newId = QUICK_PICK_ID_MIGRATIONS[pick.recordNumber]
  if (newId) {
    return { ...pick, recordNumber: newId }
  }
  return pick
}

export function loadQuickPicks(defaults: QuickPick[]): QuickPick[] {
  const stored = getLocalJSON<QuickPick[]>(QUICK_PICKS_STORAGE_KEY)
  if (!stored || stored.length === 0) return defaults

  // Apply migrations to stored picks
  const migrated = stored.map(migrateQuickPick)

  // Check if any migrations were applied
  const hasChanges = migrated.some(
    (pick, idx) => pick.recordNumber !== stored[idx].recordNumber,
  )

  // Save back if migrations were applied (keeps localStorage up to date)
  if (hasChanges) {
    storeLocalJSON(QUICK_PICKS_STORAGE_KEY, migrated)
  }

  return migrated
}

export function storeQuickPicks(picks: QuickPick[]): void {
  storeLocalJSON(QUICK_PICKS_STORAGE_KEY, picks)
}

export function loadLocationPicks(defaults: LocationPick[]): LocationPick[] {
  const stored = getLocalJSON<unknown>(LOCATION_PICKS_STORAGE_KEY)
  if (!Array.isArray(stored) || stored.length === 0) return defaults

  const normalized = stored
    .map(normalizeLocationPick)
    .filter((p): p is LocationPick => p !== null)

  if (normalized.length === 0) return defaults

  // If we dropped/normalized anything, write back to keep localStorage clean.
  if (normalized.length !== stored.length) {
    storeLocalJSON(LOCATION_PICKS_STORAGE_KEY, normalized)
  }

  return normalized
}

export function storeLocationPicks(picks: LocationPick[]): void {
  storeLocalJSON(LOCATION_PICKS_STORAGE_KEY, picks)
}

/**
 * Apply ID migration to a celestial object (used for selectedObject in settings).
 */
function migrateCelestialObject(obj: CelestialObject): CelestialObject {
  const newId = QUICK_PICK_ID_MIGRATIONS[obj.recordNumber]
  if (newId) {
    return { ...obj, recordNumber: newId }
  }
  return obj
}

export function loadSettings(): TrackerSettings | null {
  const settings = getLocalJSON<TrackerSettings>(TRACKER_SETTINGS_KEY)
  if (!settings) return null

  // Apply migration to selectedObject if it exists
  if (settings.selectedObject?.recordNumber) {
    const migrated = migrateCelestialObject(settings.selectedObject)
    if (migrated.recordNumber !== settings.selectedObject.recordNumber) {
      // Update the settings with the migrated object
      const updatedSettings = { ...settings, selectedObject: migrated }
      storeLocalJSON(TRACKER_SETTINGS_KEY, updatedSettings)
      return updatedSettings
    }
  }

  return settings
}

export function storeSettings(settings: TrackerSettings): void {
  storeLocalJSON(TRACKER_SETTINGS_KEY, settings)
}
